import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import ApiPath from "./../ConstantApi";
import { setAccessToken, logout } from "./../../Redux/Slices/AuthSlice";
import { showBanToastOnce } from "../../Utils/ShowBanModal";
import Storage from "./../../Helpers/Storage";
import Config from "./../../Config";
import { v4 as uuidv4 } from "uuid";
import { getDeviceId } from "../AuthService";

// 🔄 Queue system for multiple concurrent refreshes
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Retry delays for network-level failures: 1s, 2s, 4s
const NETWORK_RETRY_DELAYS = [1000, 2000, 4000];

function isNetworkError(status: any): boolean {
  return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
}

const RtkBaseQueryWithReauth = (baseUrl: string) => {
  return async (args: any, api: any, extraOptions: any) => {
    const baseQuery = fetchBaseQuery({
      baseUrl,
      timeout: 30000,
      prepareHeaders: (headers, { getState }) => {
        const {
          AuthSlice: { accessToken },
        }: any = getState();

        headers.set("Content-Type", "application/json");
        if (accessToken) {
          headers.set("Authorization", `Bearer ${accessToken}`);
        }

        headers.set("x-device-id", getDeviceId());

        return headers;
      },
    });

    const {
      AuthSlice: { accessToken, accessTokenExpiry, refreshToken },
    }: any = api.getState();

    const currentTime = Date.now() / 1000;

    // ✅ Handle missing expiry case by checking JWT payload
    if (accessToken && !accessTokenExpiry && refreshToken) {
      try {
        const tokenPayload = JSON.parse(atob(accessToken.split(".")[1]));
        const tokenExpiry = tokenPayload.exp;

        if (tokenExpiry && tokenExpiry <= currentTime) {
          return handleTokenRefreshWithQueue(
            baseQuery,
            args,
            api,
            extraOptions,
            refreshToken,
          );
        }
      } catch (error) {
        console.warn("Could not decode JWT token, proceeding with request");
      }
    }

    // Normal API call with retry for network-level failures
    let result = await baseQuery(args, api, extraOptions);

    // Retry on network/timeout errors with exponential backoff
    for (let attempt = 0; attempt < NETWORK_RETRY_DELAYS.length; attempt++) {
      if (!result.error || !isNetworkError(result.error.status)) break;
      await new Promise((r) => setTimeout(r, NETWORK_RETRY_DELAYS[attempt]));
      result = await baseQuery(args, api, extraOptions);
    }

    // 🔁 If unauthorized, refresh token
    if (result?.error?.status === 401 && refreshToken) {
      try {
        return handleTokenRefreshWithQueue(
          baseQuery,
          args,
          api,
          extraOptions,
          refreshToken,
        );
      } catch (error: any) {
        console.error("Token refresh error:", error);
        handleLogout(api);
        return { error: { status: 401, data: "Unauthorized" } };
      }
    }

    // 🔒 Handle suspended or inactive user
    const errorData = result?.error?.data as {
      statusCode?: number;
      message?: string;
    };

    if (
      errorData?.statusCode === 403 &&
      typeof errorData.message === "string" &&
      (errorData.message.toLowerCase().includes("inactive") ||
        errorData.message.toLowerCase().includes("suspended"))
    ) {
      const modalHeader = errorData.message.toLowerCase().includes("inactive")
        ? "Inactive"
        : "Suspended";
      showBanToastOnce(errorData.message, modalHeader);

      return {
        error: {
          status: 403,
          data: "You have been logged out due to suspension.",
        },
      };
    }

    return result;
  };
};

export default RtkBaseQueryWithReauth;

// ✅ Token refresh with queue handling
async function handleTokenRefreshWithQueue(
  baseQuery: any,
  args: any,
  api: any,
  extraOptions: any,
  refreshToken: string,
) {
  if (isRefreshing) {
    // Wait until refresh finishes
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then((token) => {
        // Retry original API call after refresh
        const headers = new Headers(args.headers);
        headers.set("Authorization", `Bearer ${token}`);
        args.headers = headers;
        return baseQuery(args, api, extraOptions);
      })
      .catch((err) => {
        return { error: { status: 401, data: "Unauthorized" } };
      });
  }

  isRefreshing = true;

  try {
    const refreshResult: any = await baseQuery(
      {
        url: ApiPath.refreshToken,
        method: "POST",
        body: { refreshToken },
      },
      api,
      extraOptions,
    );

    if (refreshResult?.data?.accessToken) {
      const newAccessToken = refreshResult.data.accessToken;
      const newRefreshToken = refreshResult.data.refreshToken || refreshToken;

      api.dispatch(
        setAccessToken({
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        }),
      );

      // Update localStorage with new tokens
      const Config = require("./../../Config").default;
      const Storage = require("./../../Helpers/Storage").default;
      Storage.set(`${Config.STORAGE_KEY}/accessToken`, newAccessToken);
      Storage.set(`${Config.STORAGE_KEY}/refreshToken`, newRefreshToken);

      // Decode and store expiry
      try {
        const tokenPayload = JSON.parse(atob(newAccessToken.split(".")[1]));
        const newExpiry = tokenPayload.exp;
        Storage.set(
          `${Config.STORAGE_KEY}/accessTokenExpiry`,
          newExpiry.toString(),
        );
      } catch (e) {
        console.warn("Could not decode new token for expiry calculation");
      }

      // Resolve queued requests
      processQueue(null, newAccessToken);

      // Retry original request
      const headers = new Headers(args.headers);
      headers.set("Authorization", `Bearer ${newAccessToken}`);
      args.headers = headers;

      return baseQuery(args, api, extraOptions);
    } else {
      throw new Error("Refresh token response invalid");
    }
  } catch (error) {
    console.error("❌ Refresh failed:", error);
    processQueue(error, null);
    handleLogout(api);
    return { error: { status: 401, data: "Unauthorized" } };
  } finally {
    isRefreshing = false;
  }
}

// 🚪 Logout helper
export function handleLogout(api: any) {
  api.dispatch(logout());
  api.dispatch(
    setAccessToken({
      accessToken: "",
      refreshToken: "",
    }),
  );
  localStorage.clear();
  sessionStorage.clear();
}
